import { modelHelperModule } from './model-helper';
import * as PercentileUtils from '../utils/percentile-utils';
import { parseDates } from '../utils/date-utils';
import {
  defineValue,
  isNullOrUndefined,
  lazyProperty,
  diff,
} from '../utils/object-utils/object-utils';
import { cleanUpQuizQuestion } from './quiz-question';
import { codeFileModelModule, configureInitiallyOpenFile } from './code-file';
import { appConfig } from '../constants/app-config';
import { hashCode } from '../utils/string-utils';
import { isArray, isDate, isObject, isString } from '../utils/type-utils';
import { setClassicFilePaths } from '../services/codemirror-workspace-service/codemirror-workspace-service';

export const CODE_SOLUTION_TYPE = 'CodeSolution';
export const ADVANCED_CODE_SOLUTION_TYPE = 'AdvancedCodeSolution';
export const QUIZ_SOLUTION_TYPE = 'QuizSolution';

const MAX_OUTPUT_LEN = 50000; // truncate output longer than this
const MAX_OUTPUT_TRUNCATE = 40000; // truncate long output to this

export const SOLUTION_FORM_DATA_IGNORED_PROPERTIES = [
  '$challengeSnapshot',
  'score',
  'challengeSnapshot',
  'accumulatedSeconds',
  'lastHeartbeat',
  'revisionCount',
  'submittedAt',
  'submittingAt',
  'failedTestNames',
  'attempts',
  'submissionStatus',
  'passingScore',
  'timerStartedAt',
  'submissionHash',
  'revisionsCount',
  'firebaseRef',
];

const SUBMISSION_STATUS_ERROR_MESSAGES = {
  invalid:
    'Unable to run test suite. Please check for errors within your code.',
  timed_out:
    'Timed Out: The solution could not be tested within the allotted time',
};

export const solutionModelModule = angular
  .module('Q.models.solution', [modelHelperModule, codeFileModelModule])

  .factory(
    'Solution',
    function (
      ModelHelper,
      $q,
      DS,
      $window,
      $log,
      AdvancedCodeSolutionFile,
      $rootScope,
    ) {
      const statuses = {
        new: 'new',
        incomplete: 'incomplete',
        completed: 'completed',
        okscore: 'ok',
        lowscore: 'lowscore',
        failed: 'failed',
        failedhidden: 'failedhidden',
      };
      const statusTitles = {
        new: 'New',
        incomplete: 'Incomplete',
        completed: 'Completed',
        okscore: 'OK Score',
        lowscore: 'Low Score',
        failed: 'Failed',
        failedhidden: 'Passed Visible, Failed Hidden',
      };
      // any quizzes scored at or below the number has the status beside it
      const scoreToStatus = [
        [60, 'failed'],
        [85, 'lowscore'],
        [95, 'okscore'],
        [Infinity, 'completed'],
      ];

      let isTeamMember = !appConfig.candidateApp;

      let Solution;

      const codeTypeMethods = (overridesAndExtras) => {
        return angular.extend(
          {
            totalTests() {
              return this.totalTestCases;
            },
            passedTests() {
              return this.passedTestCases;
            },
            _passedInfo() {
              let passed = this.passedTests();
              let total = this.totalTests();
              if (isTeamMember) {
                passed += this.hiddenPassedTestCases || 0;
                total += this.hiddenTestCases || 0;
              } else if (!this.candidateTestsEnabled() || !total) {
                return {
                  msg: this.attempted() ? 'Submitted' : 'Not Submitted',
                };
              }
              return { passed, total };
            },
            passedSummary() {
              const { passed, total, msg } = this._passedInfo();
              return msg || `${passed || 0} / ${total || '?'} Tests`;
            },
            passedSummaryLong() {
              const { passed, total, msg } = this._passedInfo();
              return msg || `${passed || 0} of ${total || '?'} tests passed`;
            },
            getStatus() {
              if (this.passedTests() > 0) {
                if (!this.passed()) {
                  return statuses.failed;
                } else if (isTeamMember && !this.passedWeighted()) {
                  return statuses.failedhidden;
                } else {
                  return statuses.completed;
                }
              } else {
                return statuses.incomplete;
              }
            },
            _output() {
              return this.output;
            },
            _response() {
              return this.response;
            },
            _wallTime() {
              return this.wallTime;
            },
            getRunResponse() {
              const totalTests = this.totalTests();
              const passed = this.passedTests();
              const output = Solution.cleanOutput(this._output());
              const failed = output ? totalTests - passed : 0;
              const wallTime = this._wallTime();
              const response = this._response();
              return angular.merge(
                {
                  $mock: !response,
                  result: {
                    passed,
                    failed,
                    output,
                    wallTime,
                    type: 'attempt',
                    successMode: this.successMode,
                    assertions: this.assertions,
                    specs: this.specs,
                    tags: this.tags,
                    completed: this.passed(),
                  },
                  stderr: '',
                  stdout: ' ',
                },
                response,
              );
            },
            passed() {
              if (typeof this.passingScore === 'boolean') {
                return this.passingScore;
              }

              return (
                this.passedTests() && this.passedTests() >= this.totalTests()
              );
            },
            passedWeighted() {
              if (this.passingScore) {
                return true;
              }

              if (this.score) return this.score >= 100;
              if (this.weightedTestCases) {
                return this.weightedPassedTestCases >= this.weightedTestCases;
              }
              return this.passed();
            },
            attempted() {
              return this.passedTests() > 0 || this.attempts > 0;
            },
            fastestWallTime() {
              if (this.codeBenchmark) {
                return this.codeBenchmark.fastestTime || this.wallTime;
              } else {
                return this.wallTime;
              }
            },
            updateScore() {
              const totalTests = this.totalTests();
              if (totalTests > 0) {
                this.score = ((this.passedTests() || 0) / totalTests) * 100;
              }
            },
            loadRevisions(force) {
              if (
                force ||
                !angular.isArray(this.revisions) ||
                !this.revisions.length
              ) {
                if (this.isAdvancedCode() || $rootScope.playbackToken) {
                  return DS.definitions.ExternalRevision.loadFor(this);
                } else {
                  return Solution.loadRevisions(this);
                }
              } else {
                this.revisions = parseDates(this.revisions);
                return $q.when(this.revisions);
              }
            },
            logAttempt(data) {
              return Solution.logAttempt(this, { data });
            },
            logTest(data) {
              return Solution.logTest(this, { data });
            },
            hasTags(tag) {
              if (tag !== undefined) {
                return !!this.tagProps(tag).total;
              } else {
                return (
                  this.usedSmartIde ||
                  this.usedExternalIde ||
                  this.hasTags('') ||
                  !!(this.tags && Object.keys(this.tags).length)
                );
              }
            },
            hiddenProps() {
              return {
                passed: this.hiddenPassedTestCases,
                total: this.hiddenTestCases,
              };
            },
            tagProps(tag) {
              let tagProps =
                (tag ? this.tags && this.tags[tag] : this.hiddenProps()) || {};
              let passed = (tagProps.passed = tagProps.passed || 0);
              let total =
                tagProps.total ||
                (tag && this.tagTestCases
                  ? this.tagTestCases[tag]
                  : this.hiddenTestCases) ||
                0;
              tagProps.failed = Math.max(
                tagProps.failed || 0,
                total - passed || 0,
              );
              tagProps.total = total || 0;
              return tagProps;
            },
            tagClass(tag) {
              let tagProps = this.tagProps(tag);
              if (tagProps.passed >= tagProps.total) return 'success';
              if (tagProps.passed === 0 && tagProps.failed) return 'danger';
              if (tagProps.passed && tagProps.failed) return 'warning';
              return '';
            },
            tagSummary(tag) {
              let tagProps = this.tagProps(tag);
              return `${tagProps.passed} of ${tagProps.total || '?'}`;
            },
            candidateTestsEnabled() {
              return (
                !this.challenge ||
                this.challenge.candidateTestsEnabled !== false
              );
            },
            hasFailedSubmissionAttempts() {
              return !!(
                !this.expired() &&
                this.challenge &&
                this.challenge.limitSubmissionAttempts &&
                this.submissionFailedMessages().length
              );
            },
            updateSubmissionFailedMessages(reviewing) {
              const errs = (this.$submissionFailedMessages = []);
              if (this.submissionResponseError) {
                errs.push(
                  reviewing
                    ? 'An exception was caught running the code.'
                    : 'An exception was caught within your code. Please check for issues that could cause the program to fail to run properly.',
                );
              }
              errs.push(...(this.failedTestNames || []));
              const statusError =
                SUBMISSION_STATUS_ERROR_MESSAGES[this.submissionStatus];
              if (statusError) {
                errs.push(statusError);
              }
              if (
                !errs.length &&
                this.isSubmitted() &&
                this.passingScore === false
              ) {
                // if we have no errors and we submitted
                errs.push(
                  reviewing
                    ? 'Not all of the expected tests completed'
                    : 'Not all of the expected tests completed, please make sure your code is not preventing tests from running',
                );
              }
              return errs;
            },
            submissionFailedMessages(reviewing) {
              return (
                this.$submissionFailedMessages ||
                this.updateSubmissionFailedMessages(reviewing)
              );
            },
          },
          overridesAndExtras,
        );
      };

      Solution = ModelHelper.defineResource({
        name: 'Solution',
        endpoint: 'solutions',

        staticMethods: {
          /**
           * Configures initial layout for challenge config and editor tool tabs
           * @param {multiFileChallengeService} mfcs - Source of tabs
           * @param {boolean} always - If true, always set the layout, even if the tab has a different one stored
           * @param {boolean} lockedLayout - If true, set the layout if missing from the tab
           */
          configureInitialLayout(mfcs, { always, lockedLayout }) {
            const props = mfcs.challenge.initialLayout;
            []
              .concat(mfcs.challengeConfig, mfcs.challengeEditorTools)
              .forEach((file) => {
                configureInitiallyOpenFile({
                  file,
                  always,
                  lockedLayout,
                  initialLayout: props[file.id],
                });
              });
          },
          /**
           * Destructively cleans up any problems with extremely long log lines in the output
           * @param output
           * @returns {*}
           */
          cleanOutput(output) {
            angular.forEach(output, (v, k) => {
              if (isString(v) && v.length > MAX_OUTPUT_LEN) {
                output[k] =
                  `${v.slice(0, MAX_OUTPUT_TRUNCATE)}\n[Truncated ${v.length - MAX_OUTPUT_TRUNCATE} characters]`;
              } else if (isObject(v) || isArray(v)) {
                output[k] = Solution.cleanOutput(v);
              }
            });
            return output;
          },
        },

        methods: {
          getStatusTitle() {
            return statusTitles[this.getStatus()];
          },
          isCode() {
            return this.isClassicCode() || this.isAdvancedCode();
          },
          isClassicCode() {
            return this.$type === CODE_SOLUTION_TYPE;
          },
          isAdvancedCode() {
            return this.$type === ADVANCED_CODE_SOLUTION_TYPE;
          },
          isQuiz() {
            return this.$type === QUIZ_SOLUTION_TYPE;
          },
          getChallengeSnapshot() {
            if (!this.$challengeSnapshot && this.challengeSnapshot) {
              this.$challengeSnapshot = DS.createInstance(
                'Challenge',
                this.challengeSnapshot,
              );
              if (this.$challengeSnapshot.questions) {
                this.$challengeSnapshot.questions =
                  this.$challengeSnapshot.questions
                    .map((q) =>
                      DS.createInstance('QuizQuestion', cleanUpQuizQuestion(q)),
                    )
                    .sort((a, b) => a.position - b.position);
              }
            }
            return this.$challengeSnapshot || this.challenge;
          },
          // dummy method for non-code solutions
          getRunResponse() {
            return {
              $mock: true,
              result: {
                passed: 0,
                failed: 0,
                wallTime: 0,
                type: 'attempt',
              },
              stderr: '',
              stdout: ' ',
            };
          },
          percentile(type) {
            return PercentileUtils.getPercentile(
              this[type],
              this.challenge && this.challenge[type + 'Stats'],
            );
          },
          hasStats(type) {
            return (
              !!this.challenge && this.challenge.hasStats(type, this.language)
            );
          },
          showStats(type, value) {
            // some pieces of information are tracked under the similarity object
            const sim = this.similarity || {};
            if (type === 'similarity') {
              value = sim.maxSimilarity;
            } else if (type === 'mass') {
              value = sim[type];
            } else {
              value = this[type];
            }
            return (
              !!this.score && !isNullOrUndefined(value) && this.hasStats(type)
            );
          },
          blindedSolutionReviews() {
            // if we should be hiding assessment reviews for blind reviews, then we will hide
            // the solution reviews as well.
            if (
              this.assessmentResult &&
              this.assessmentResult.shouldHideOtherReviews()
            ) {
              const arr = this.assessmentResult.myReview();
              return arr ? arr.solutionReviews : [];
            }

            return this.solutionReviews;
          },
          // true if the candidate's scored notes can no longer be edited. Mirrors
          // the server, which allows a 45s grace window for the last autosave.
          notesLocked() {
            return !!(
              this.challenge &&
              this.challenge.candidateNotesScored &&
              this.challenge.isTimed() &&
              !this.pendingTimerReset &&
              this.timerStartedAt &&
              new Date(this.timerStartedAt).getTime() +
                this.challenge.timeLimitMin * 60000 +
                45000 <
                new Date()
            );
          },
          expired() {
            // Convert timerStartedAt to Date if it's a string (handles FormData sync issues)
            if (
              this.challenge &&
              this.challenge.isTimed() &&
              !this.pendingTimerReset &&
              isString(this.timerStartedAt)
            ) {
              this.timerStartedAt = new Date(this.timerStartedAt);
            }

            if (
              this.challenge &&
              this.challenge.isTimed() &&
              !this.pendingTimerReset &&
              !isDate(this.timerStartedAt)
            ) {
              $log.error(
                `this.timerStartedAt is not a date`,
                this.timerStartedAt,
              );
              return;
            }
            // returns true if this solution can no longer be edited
            return (
              this.challenge &&
              ((this.challenge.isTimed() &&
                !this.pendingTimerReset &&
                this.timerStartedAt.getTime() +
                  this.challenge.timeLimitMin * 60000 <
                  new Date()) ||
                (this.isAdvancedCode() && this.remainingAttempts() < 1))
            );
          },
        },

        relations: {
          belongsTo: {
            Assessment: {
              localField: 'assessment',
              localKey: 'assessmentId',
            },
            AssessmentResult: {
              localKey: 'assessmentResultId',
              localField: 'assessmentResult',
            },
            Candidate: {
              localField: 'candidate',
              localKey: 'candidateId',
            },
            Challenge: {
              localKey: 'challengeId',
              localField: 'challenge',
            },
          },
          hasMany: {
            SolutionReview: {
              localField: 'solutionReviews',
              foreignKey: 'solutionId',
            },
            AdvancedCodeSolutionFile: {
              $type: 'AdvancedCodeSolution',
              localField: 'files',
              link: false,
            },
          },
        },

        meta: {
          hasDigest: true,
          cleanInjected(instance) {
            if (!$rootScope.playbackToken) {
              delete instance.revisions;
            }
            delete instance.$changedFiles;
            delete instance.$submissionFailedMessages;
          },
          fetchOnPush: (data) => {
            // only fetch solution if we have the assessment result
            return DS.get('AssessmentResult', data.assessmentResultId || '');
          },
          includeOnPull: ['digest', 'similarity'],
          saveExcludes: [
            'challenge',
            'assessmentResult',
            'candidate',
            'teamCandidate',
            'challengeSnapshot',
            '$challengeSnapshot',
            'failedTestNames',
            'revisionsCount',
          ],
          types: {
            CodeSolution: codeTypeMethods({
              // Mock files array to simulate an AdvancedCodeSolution
              afterTyped(instance) {
                const createFile = (field) => {
                  // const testCases = /testcases/i.test(field);
                  const file = AdvancedCodeSolutionFile.createInstance({
                    id: field.toLowerCase(),
                    field,
                    deletable: false,
                    access: 'readwrite',
                    isClassicCode: true,
                  });
                  Object.defineProperty(file, 'contents', {
                    get() {
                      return instance[field];
                    },
                    set(val) {
                      instance[field] = val;
                    },
                    configurable: true,
                  });
                  return file;
                };
                lazyProperty(instance, 'files', () => {
                  const preloaded = createFile('preloaded');
                  preloaded.access = 'readonly';
                  const files = [
                    createFile('code'),
                    createFile('testCases'),
                    preloaded,
                  ];

                  files.updatePaths = () => {
                    const challenge = instance.challenge;
                    const runConfig =
                      (challenge && challenge._runConfig()) || {};
                    // use this time to update preloaded
                    const lang = instance.challengeLanguage();
                    preloaded.contents = (lang && lang.preloaded) || '';
                    return setClassicFilePaths({
                      files,
                      language: instance.language,
                      testFramework: runConfig.testFramework,
                      languageVersion: runConfig.languageVersion,
                      $log,
                    });
                  };

                  files.updatePaths();
                  // TODO: do we ever need to track renamed files here?
                  instance.on('afterInject', files.updatePaths);

                  return files;
                });
              },
              challengeLanguage() {
                const challenge = this.getChallengeSnapshot();
                return challenge && challenge.language(this.language);
              },
              isSubmitting() {
                return false;
              },
              isSubmitted() {
                return this.attempted();
              },
            }),
            AdvancedCodeSolution: codeTypeMethods({
              totalTests() {
                return isTeamMember
                  ? this.totalTestCases
                  : this.testTotalTestCases;
              },
              passedTests() {
                return isTeamMember
                  ? this.passedTestCases
                  : this.testPassedTestCases;
              },
              attempted() {
                const attempts = isTeamMember
                  ? this.attempts
                  : this.testAttempts;
                return (
                  this.passedTests() > 0 || attempts > 0 || this.isSubmitted()
                );
              },
              remainingAttempts() {
                if (this.challenge && this.challenge.limitSubmissionAttempts) {
                  const attempts = this.attempts || 0;
                  return Math.max(
                    0,
                    this.challenge.limitSubmissionAttempts - attempts,
                  );
                } else {
                  return Infinity;
                }
              },
              _output() {
                return isTeamMember ? this.output : this.testOutput;
              },
              _wallTime() {
                return isTeamMember ? this.wallTime : this.testWallTime;
              },
              _response() {
                return isTeamMember ? this.response : this.testResponse;
              },
              changedFiles() {
                const solution = this;
                const challenge = solution.getChallengeSnapshot();
                const challengeFiles = (challenge && challenge.files) || [];
                if (!solution.hasOwnProperty('$changedFiles')) {
                  defineValue(
                    solution,
                    '$changedFiles',
                    solution.files.filter((sf) => {
                      const cf = challengeFiles.find(
                        (cf) =>
                          cf.id === sf.challengeFileId || cf.path === sf.path,
                      );
                      return (
                        !cf ||
                        (cf.$access === 'readwrite' &&
                          cf.contents !== sf.contents)
                      );
                    }),
                  );
                }
                return solution.$changedFiles;
              },
              isCompleted() {
                return !!this.submissionStatus;
              },
              isSubmitting() {
                return (
                  this.submissionStatus === 'submitting' || this.isRetrying()
                );
              },
              isRetrying() {
                return this.submissionStatus === 'retrying';
              },
              isSubmitted() {
                return !!this.submissionStatus && !this.isSubmitting();
              },
              isSubmissionSuccess() {
                return this.submissionStatus === 'successful';
              },
              isSubmissionInvalid() {
                return this.submissionStatus === 'invalid';
              },
              isSubmissionTimeout() {
                return this.submissionStatus === 'timed_out';
              },
              generateSubmissionHash() {
                const data = this.files
                  .map((f) => f.path + '\n' + f.contents)
                  .join('\n');
                return hashCode(data);
              },
              download(user) {
                try {
                  const params = {
                    x_login_type: user.loginType,
                    $type: this.$type,
                  };
                  const url = Solution.getEndpointUrl({
                    id: this,
                    segment: 'download',
                    params,
                  });
                  if (!$window.open(url)) {
                    throw new Error('Unable to open a new window');
                  }
                } catch (ex) {
                  $window.alert('Unable to open the download file: ' + ex);
                }
              },
            }),
            QuizSolution: {
              answered(index) {
                const _didAnswer = (a) =>
                  !!(a && a.values && (a.values[0] || a.values[0] === 0));
                const answers = this.answers || [];
                if (angular.isNumber(index)) {
                  return _didAnswer(answers[index]);
                } else {
                  return answers.reduce((sum, a) => {
                    if (_didAnswer(a)) {
                      sum++;
                    }
                    return sum;
                  }, 0);
                }
              },
              answeredCorrectly() {
                return (this.answers || []).reduce((sum, a) => {
                  if (a.score === 100) {
                    sum++;
                  }
                  return sum;
                }, 0);
              },
              totalTests() {
                const challenge = this.getChallengeSnapshot();
                const questions = challenge && challenge.questions;
                if (questions) {
                  return questions.length;
                } else {
                  // guestimate
                  return (this.answers || []).length;
                }
              },
              passedSummary() {
                return `${isTeamMember ? this.answeredCorrectly() : this.answered()} / ${this.totalTests()} Questions`;
              },
              passedSummaryLong() {
                return `${isTeamMember ? this.answeredCorrectly() : this.answered()} of ${this.totalTests()} questions answered correctly`;
              },
              getStatus() {
                let answered = this.answered();
                let total = this.totalTests();
                if (this.passed()) {
                  // got 100% or answered every question as a candidate
                  return statuses.completed;
                } else if (answered < total) {
                  // didn't answer all
                  return statuses.incomplete;
                } else {
                  // answered all && didn't get 100%
                  return scoreToStatus.find((ss) => this.score <= ss[0])[1];
                }
              },
              allAnswered() {
                return this.answered() >= this.totalTests();
              },
              passed() {
                return this.score !== undefined
                  ? this.score === 100
                  : this.allAnswered();
              },
              attempted() {
                return this.answered() > 0;
              },
              // returns the first question that needs an answer still, or -1 if there are no
              needsAnswerIdx(
                challenge = this.getChallengeSnapshot(),
                answers = this.answers || [],
              ) {
                return answers.findIndex((ans) => {
                  const q = challenge.questions.find(
                    (q) => q.id === ans.questionId,
                  );
                  return (
                    q &&
                    !ans.completedAt &&
                    (!ans.startedAt ||
                      new Date() - ans.startedAt <
                        q._timeLimitSec(challenge) * 1000)
                  );
                });
              },
              canStillAnswer(challenge = this.getChallengeSnapshot()) {
                if (challenge.isTimed() && this.timerStartedAt) {
                  const timeoutMs =
                    challenge.timeLimitMin * 60 * 1000 -
                    (new Date() - this.timerStartedAt);
                  return timeoutMs > 0;
                } else if (challenge.isQuestionTimed()) {
                  return this.needsAnswerIdx(challenge) > -1;
                }
                return true;
              },
              hasEmptyMultipleChoice() {
                const challenge = this.getChallengeSnapshot();
                const questions = challenge.questions || [];
                return (
                  this.canStillAnswer(challenge) &&
                  questions.some((q) => {
                    let a = (this.answers || []).find(
                      (a) => a.questionId === q.id,
                    );
                    return (
                      q.isMultipleChoice() &&
                      (!a || !a.values || a.values.length === 0)
                    );
                  })
                );
              },
              updateScore: angular.noop,
              hasTags() {
                return false;
              },
              tagSummary() {
                return '';
              },
            },
          },
          actionEndpoints: {
            resetTimer: { method: 'PUT' },
            reset: { method: 'PUT' },
            resetTestCases: { method: 'PUT' },
            resetSolution: { method: 'PUT' },
            logAttempt: { method: 'PUT' },
            logTest: { method: 'PUT' },
            submit: { method: 'PUT', ignoreLoadingBar: true },
            heartbeat: {
              method: 'PUT',
              ignoreLoadingBar: true,
              ignoreNetworkErrors: true,
              log: false,
              debug: false,
            },
            loadRevisions: {
              method: 'GET',
              segment: 'revisions',
              responseHandler: function (res, Model, instance) {
                if (angular.isArray(res.data.data)) {
                  return (instance.revisions = parseDates(res.data.data));
                } else {
                  return $q.reject();
                }
              },
            },
            updateAuthenticity: {
              method: 'PATCH',
              segment: 'authenticity',
              responseHandler: function (res, Model, instance) {
                // Update the instance's authenticity with the response data
                if (res.data && res.data.data && res.data.data.authenticity) {
                  instance.authenticity = res.data.data.authenticity;
                }
                return instance;
              },
            },
          },
          excludeSaveOrCreateProps: ['files'],
          includeSaveOrCreateChange(k, value) {
            return k !== 'cursor' && (k !== 'tag' || value !== 'autosave');
          },
          afterSaveOrCreate(instance, changes, requestOptions) {
            let fields = [];
            if (instance.$type === 'AdvancedCodeSolution') {
              // Ensure files with softDeletedAt are included in the updates
              const files = instance.files || [];
              const updates = changes && changes.files ? changes.files : [];

              // Add files with softDeletedAt to the updates if they are not already included
              files.forEach((file) => {
                if (
                  file.softDeletedAt &&
                  !updates.some((update) => update.id === file.id)
                ) {
                  updates.push(file);
                }
              });

              // If there are updates, ensure they are included in the changes
              if (updates.length > 0 && changes) {
                changes.files = updates;
              }

              fields = {
                Model: DS.definitions.AdvancedCodeSolutionFile,
                fieldName: 'files',
                foreignKey: 'solutionId',
                changes,
                requestOptions,
                orderable: false,
                exclude: (file) =>
                  file.encrypted ||
                  (file.$access !== 'readwrite' &&
                    file.$access !== 'readonly' &&
                    !file.softDeletedAt),
                diff(file, fileChanges) {
                  // If the file has been soft deleted, make sure to include that in the diff
                  if (fileChanges.softDeletedAt) {
                    return { softDeletedAt: fileChanges.softDeletedAt };
                  }

                  const uiChanges = {
                    options: fileChanges.options,
                    cursor: fileChanges.cursor,
                  };
                  // only allow UI settings to be stored for readonly files
                  return diff(
                    file,
                    file.$access !== 'readwrite' ? uiChanges : fileChanges,
                  );
                },
                uniqueChildField: 'path',
              };
            }
            return ModelHelper.saveEmbedded(fields);
          },
        },
      });

      return Solution;
    },
  );
